import { useState, useEffect } from "react";
import { Heart, Sparkles, ArrowRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Kuta score interpretations
const SCORE_LABELS = {
  excellent: { min: 28, label: "Excellent", color: "text-emerald-400", bg: "bg-emerald-500/20" },
  good: { min: 21, label: "Good", color: "text-green-400", bg: "bg-green-500/20" },
  average: { min: 14, label: "Average", color: "text-amber-400", bg: "bg-amber-500/20" },
  challenging: { min: 0, label: "Challenging", color: "text-rose-400", bg: "bg-rose-500/20" },
};

function getScoreLabel(score) {
  if (score >= 28) return SCORE_LABELS.excellent;
  if (score >= 21) return SCORE_LABELS.good;
  if (score >= 14) return SCORE_LABELS.average;
  return SCORE_LABELS.challenging;
}

export default function QuickCompatibility() {
  const [step, setStep] = useState(1);
  const [person1, setPerson1] = useState({ name: "", birthDate: "", birthTime: "", birthCity: "" });
  const [person2, setPerson2] = useState({ name: "", birthDate: "", birthTime: "", birthCity: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCalculate = async () => {
    if (!person1.birthDate || !person1.birthCity || !person2.birthDate || !person2.birthCity) {
      setError("Please fill in birth date and city for both people");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/compatibility/quick`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          person1_name: person1.name || "Person 1",
          person1_birth_date: person1.birthDate,
          person1_birth_time: person1.birthTime || "12:00",
          person1_birth_city: person1.birthCity,
          person2_name: person2.name || "Person 2",
          person2_birth_date: person2.birthDate,
          person2_birth_time: person2.birthTime || "12:00",
          person2_birth_city: person2.birthCity,
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || "Calculation failed");
      }

      const data = await response.json();
      setResult(data);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setPerson1({ name: "", birthDate: "", birthTime: "", birthCity: "" });
    setPerson2({ name: "", birthDate: "", birthTime: "", birthCity: "" });
    setResult(null);
    setError(null);
  };

  return (
    <div 
      className="bg-gradient-to-br from-rose-950/30 to-pink-900/20 border border-rose-800/30 rounded-2xl p-6"
      data-testid="quick-compatibility"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <div className="p-2 rounded-lg bg-rose-500/20">
          <Heart className="w-5 h-5 text-rose-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-rose-200">Quick Compatibility Check</h3>
          <p className="text-xs text-white/40">Instant nakshatra-based score</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Step 1: Person 1 */}
      {step === 1 && (
        <div className="space-y-4">
          <p className="text-white/60 text-sm mb-4">Enter first person's details:</p>
          
          <Input
            placeholder="Name (optional)"
            value={person1.name}
            onChange={(e) => setPerson1({ ...person1, name: e.target.value })}
            className="bg-black/30 border-white/10 text-white"
            data-testid="person1-name"
          />
          
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="date"
              value={person1.birthDate}
              onChange={(e) => setPerson1({ ...person1, birthDate: e.target.value })}
              className="bg-black/30 border-white/10 text-white"
              data-testid="person1-date"
            />
            <Input
              type="time"
              value={person1.birthTime}
              onChange={(e) => setPerson1({ ...person1, birthTime: e.target.value })}
              className="bg-black/30 border-white/10 text-white"
              placeholder="Time (optional)"
              data-testid="person1-time"
            />
          </div>
          
          <CityAutocomplete
            placeholder="Birth City (e.g., Mumbai, India)"
            value={person1.birthCity}
            onChange={(val) => setPerson1({ ...person1, birthCity: val })}
            className="bg-black/30 border-white/10 text-white"
            testId="person1-city"
          />

          <Button
            onClick={() => person1.birthDate && person1.birthCity ? setStep(2) : setError("Birth date and city required")}
            className="w-full bg-rose-600 hover:bg-rose-500"
            data-testid="next-to-person2"
          >
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Step 2: Person 2 */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-white/60 text-sm">Enter second person's details:</p>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setStep(1)}
              className="text-white/40 hover:text-white/60"
            >
              Back
            </Button>
          </div>
          
          <Input
            placeholder="Name (optional)"
            value={person2.name}
            onChange={(e) => setPerson2({ ...person2, name: e.target.value })}
            className="bg-black/30 border-white/10 text-white"
            data-testid="person2-name"
          />
          
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="date"
              value={person2.birthDate}
              onChange={(e) => setPerson2({ ...person2, birthDate: e.target.value })}
              className="bg-black/30 border-white/10 text-white"
              data-testid="person2-date"
            />
            <Input
              type="time"
              value={person2.birthTime}
              onChange={(e) => setPerson2({ ...person2, birthTime: e.target.value })}
              className="bg-black/30 border-white/10 text-white"
              placeholder="Time (optional)"
              data-testid="person2-time"
            />
          </div>
          
          <CityAutocomplete
            placeholder="Birth City (e.g., Delhi, India)"
            value={person2.birthCity}
            onChange={(val) => setPerson2({ ...person2, birthCity: val })}
            className="bg-black/30 border-white/10 text-white"
            testId="person2-city"
          />

          <Button
            onClick={handleCalculate}
            disabled={loading}
            className="w-full bg-rose-600 hover:bg-rose-500"
            data-testid="calculate-compatibility"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 animate-spin" /> Calculating...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Heart className="w-4 h-4" /> Check Compatibility
              </span>
            )}
          </Button>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && result && (
        <div className="space-y-4" data-testid="compatibility-result">
          {/* Score display */}
          <div className="text-center py-4">
            <div className="relative inline-block">
              <div className={`text-6xl font-bold ${getScoreLabel(result.total_score).color}`}>
                {result.total_score}
              </div>
              <div className="text-white/40 text-sm">out of 36</div>
            </div>
            <div className={`mt-2 inline-block px-3 py-1 rounded-full text-sm font-medium ${getScoreLabel(result.total_score).bg} ${getScoreLabel(result.total_score).color}`}>
              {getScoreLabel(result.total_score).label} Match
            </div>
          </div>

          {/* Names */}
          <div className="flex items-center justify-center gap-3 text-white/60 text-sm">
            <span>{result.person1_name || "Person 1"}</span>
            <Heart className="w-4 h-4 text-rose-400" />
            <span>{result.person2_name || "Person 2"}</span>
          </div>

          {/* Key scores */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            {result.key_scores?.map((score, i) => (
              <div key={i} className="p-2 rounded-lg bg-black/20 text-center">
                <div className="text-lg font-semibold text-white/80">{score.points}/{score.max}</div>
                <div className="text-xs text-white/40">{score.name}</div>
              </div>
            ))}
          </div>

          {/* Summary */}
          {result.summary && (
            <p className="text-white/60 text-sm text-center pt-2">
              {result.summary}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={resetForm}
              className="flex-1 border-white/20 text-white/60 hover:bg-white/5"
            >
              Try Another
            </Button>
            <Link to="/compatibility" className="flex-1">
              <Button className="w-full bg-rose-600 hover:bg-rose-500">
                Full Report <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
